import {useQueryClient} from "@tanstack/react-query";
import React, {useEffect, useRef, useState} from 'react';
import Dialog from '../../../../components/dialogs/Dialog';
import DialogTitle from '../../../../components/dialogs/DialogTitle';
import DialogContent from '../../../../components/dialogs/DialogContent';
import TextField from '../../../../components/fields/textfield/TextField';
import Grid from '../../../../components/dialogs/Grid';
import {t} from '../../../../store/localization/TranslMessasge';
import {CommonTranslationKey} from '../CommonTranslationKey';
import {CoreDataType, DataTypeDefinitionUpdateDto} from '../../../../common/apis/datatype/DataTypeDefinitionDto';
import {ButtonLayout, NegativeButtons, PositiveButtons} from "../../../../components/button/ButtonLayout";
import {CancelButton} from "../../../../components/button/CancelButton";
import DialogActions from "../../../../components/dialogs/DialogActions";
import Snackbar from "../snackbar/Snackbar";
import {ErrorTranslationKey} from "../ErrorTranslationKey";
import {SaveButton} from "../../../../components/button/SaveButton";
import {StereotypeDto, StereotypeUpdateDto} from "../../../../common/apis/stereotype/StereotypeDto";
import {StereotypeApiClient} from '../../../../common/apis/stereotype/StereotypeApiClient';
import {invalidateFindAllStereotypesQueryCache} from "./StereotypeQuery";
import {StereotypesTranslationKey} from "./StereotypesTranslationKey";
import {ArchimateElement} from "../../../../common/archimate/ArchimateElement";

interface StereotypeUpdateDialogProps {
    open: boolean;
    stereotype: StereotypeDto;
    onUpdated: (stereotypeId: string) => void;
    onClose: () => void;
}

export function StereotypeUpdateDialog({ open, stereotype, onUpdated, onClose }: Readonly<StereotypeUpdateDialogProps>) {
    const stereotypeApiClient = useRef(new StereotypeApiClient());

    const [stereotypeUpdate, setStereotypeUpdate] = useState<Partial<StereotypeUpdateDto>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [inProgress, setInProgress] = useState(false);

    const queryClient = useQueryClient();

    useEffect(() => {
        if (open) {
            setStereotypeUpdate({
                name: stereotype.name,
                description: stereotype.description
            });
            setErrors({});
        }
    }, [open, stereotype]);

    function updateStereotype() {
        if (!validateForm()) {
            return;
        }

        setInProgress(true);

        const trimmedUpdate: DataTypeDefinitionUpdateDto = {
            name: stereotypeUpdate.name!.trim(),
            description: stereotypeUpdate.description?.trim()
        };

        stereotypeApiClient.current.updateStereotype(stereotype.id, trimmedUpdate)
            .then(() => {
                onUpdated(stereotype.id);
                onClose();
                invalidateFindAllStereotypesQueryCache(queryClient);
                Snackbar.success(t(CommonTranslationKey.DATA_SAVED_SUCCESSFULLY));
            })
            .catch((error) => Snackbar.error(t(ErrorTranslationKey.UNEXPECTED_ERROR_OCCURRED), error))
            .finally(() => setInProgress(false));
    }

    function validateForm(): boolean {
        const newErrors: Record<string, string> = {};
        if (!stereotypeUpdate.name?.trim()) {
            newErrors.name = t(CommonTranslationKey.FILL_OUT_THIS_FIELD);
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    function formatElementType(elementType: string): string {
        return ArchimateElement.findByStandardName(elementType)?.visibleName ?? "";
    }

    let coreDataTypes = Object.values(CoreDataType);
    coreDataTypes.sort();

    return (
            <Dialog open={open} onClose={onClose} maxWidth="md">
                <DialogTitle title={t(StereotypesTranslationKey.EDIT_STEREOTYPE)}
                        onDialogClosed={onClose} />
                <DialogContent>
                    <Grid container>
                        <Grid item xs={12} sm={7}>
                            <TextField
                                    label={t(StereotypesTranslationKey.NAME)}
                                    value={stereotypeUpdate.name}
                                    onChange={value => setStereotypeUpdate({...stereotypeUpdate, name: value})}
                                    errorMessage={errors.name}
                                    required={true}/>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                    label={t(StereotypesTranslationKey.BASE_TYPE)}
                                    value={formatElementType(stereotype.elementType)}
                                    readOnly={true}
                                    fullWidth/>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                    label={t(CommonTranslationKey.DESCRIPTION)}
                                    value={stereotypeUpdate.description ?? ''}
                                    onChange={value => setStereotypeUpdate({...stereotypeUpdate, description: value})}
                                    multiline
                                    rows={4}/>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <ButtonLayout>
                        <PositiveButtons>
                            <SaveButton onClick={updateStereotype} disabled={inProgress} />
                        </PositiveButtons>
                        <NegativeButtons>
                            <CancelButton onClick={onClose} disabled={inProgress} />
                        </NegativeButtons>
                    </ButtonLayout>
                </DialogActions>
            </Dialog>
    );
}
